import { defineStore } from 'pinia'
import { ref } from 'vue'

import Toast from '../models/Toast'
import { Queue } from '@/utils/queueUtils'
import { MessageEnum } from '../models/enums'

export const useToastStore = defineStore('toastStore', () => {
  const toastQueue = ref(new Queue<Toast>())
  const timers = new Map<string, NodeJS.Timeout>()

  function successToast(message: string, isModal = false, displayTime = 8000) {
    _addToast(message, { type: MessageEnum.SUCCESS, displayTime, isModal })
  }
  function warningToast(message: string, isModal = false, displayTime = 20000) {
    _addToast(message, { type: MessageEnum.WARNING, displayTime, isModal })
  }
  function errorToast(message: string, isModal = false, displayTime = 20000) {
    _addToast(message, { type: MessageEnum.ERROR, displayTime, isModal })
  }

  function _addToast(message: string, config: Pick<Toast, 'type' | 'isModal' | 'displayTime'>) {
    const toast = new Toast({ message, ...config })

    toastQueue.value.enqueue(toast)

    if (toastQueue.value.size() > 2) {
      toastQueue.value.dequeue()
    }

    if (!toast.autoClose) return

    const timer = setTimeout(() => {
      removeToast(toast.id)
    }, toast.displayTime)
    timers.set(toast.id, timer)
  }

  function removeToast(id: string) {
    const newQueue = toastQueue.value.show().filter((toast) => toast.id !== id)
    toastQueue.value = new Queue<Toast>(newQueue)
  }

  function pauseToast(id: string) {
    if (!timers.has(id)) return

    clearTimeout(timers.get(id))
  }

  function resumeToast(id: string) {
    if (!timers.has(id)) return

    const timer = setTimeout(
      () => removeToast(id),
      toastQueue.value.show().find((toast) => toast.id === id)?.displayTime
    )

    timers.set(id, timer)
  }

  function getHeaderToasts() {
    return toastQueue.value.show().filter((m) => !m.isModal)
  }
  function getModalToasts() {
    return toastQueue.value.show().filter((m) => !!m.isModal)
  }

  function clearAllHeaderToasts() {
    const toasts = getHeaderToasts()
    toasts.forEach((toast) => removeToast(toast.id))
  }

  function clearAllModalToasts() {
    const toasts = getModalToasts()
    toasts.forEach((toast) => removeToast(toast.id))
  }

  return {
    successToast,
    warningToast,
    errorToast,
    removeToast,
    pauseToast,
    resumeToast,
    getHeaderToasts,
    getModalToasts,
    clearAllHeaderToasts,
    clearAllModalToasts
  }
})
